<script lang="ts">
  import {
    tasks, det, wbsNodes, editingTaskId, newTaskDraft, createTask, closeEditModal, scheduleSave, showToast, isEditor, canEdit,
    subcontractorColors, risks, addRisk, updateRisk, statusRows, twoShifts, allowWeekendWork, projectPhases,
    knownSubcontractors, knownEquipment, knownPersonnel,
    addKnownSubcontractor, removeKnownSubcontractor, addKnownEquipment, removeKnownEquipment, addKnownPersonnel, removeKnownPersonnel,
  } from "../lib/stores";
  import { SHIFT_LABEL } from "../lib/shifts";
  import type { Shift } from "../lib/shifts";
  import { subcontractorColor } from "../lib/subcontractors";
  import { fmt1 } from "../lib/format";
  import type { Phase, ProbabilityImpact, ResponsibleParty, RiskEntry, RiskStatus, Subcontractor, Task, TaskType, WeekendMode } from "../lib/types";
  import WbsSelect from "./WbsSelect.svelte";
  import ChipPicker from "./ChipPicker.svelte";
  import { wbsLabel, workPackages } from "../lib/wbs";
  import { splitList } from "../lib/board";
  import { inputValue, selectValue } from "../lib/dom";
  import {
    riskLevel, risksLinkedInText, addRiskReference, removeRiskReference,
    PROBABILITY_IMPACT_OPTIONS, RISK_STATUS_OPTIONS,
  } from "../lib/risk";
  import { get } from "svelte/store";

  const RESPONSIBLE_PARTY_OPTIONS: ResponsibleParty[] = ["", "RWG", "ABB"];

  $: creating = $newTaskDraft !== null;
  $: task = $newTaskDraft ?? ($editingTaskId ? get(tasks).find((t) => t.id === $editingTaskId) ?? null : null);
  $: readOnly = !$canEdit;

  // Local editable copy of the form fields — same pattern as the original modal
  // (populate on open, write back only on Save).
  let wbsId: string | null = null;
  let name = "", type: TaskType = "activity", phase: Phase = "";
  let preds: string[] = [];
  let o = 0, m = 0, p = 0, pinnedDay = "";
  let actualStart = "", actualFinish = "";
  let pinnedShift: Shift | null = null;
  let doubleShift = false;
  let weekendWork: WeekendMode = "auto";
  let subcontractors: Subcontractor[] = [];
  let responsibleParty: ResponsibleParty = "";
  let equipmentList: string[] = [];
  let personnelList: string[] = [];
  let requiredStatus: { rowId: string; value: boolean }[] = [];
  let riskText = "";
  let predSearch = "";

  let activeSubTab: "details" | "risks" = "details";
  let linkRiskId = "";
  let expandedRiskIds = new Set<string>();
  let showNewRiskForm = false;
  let newRiskDesc = "";
  let newRiskProbability: ProbabilityImpact = "";
  let newRiskImpact: ProbabilityImpact = "";
  let newRiskOwner = "";
  let newRiskMitigation = "";
  let newRiskStatus: RiskStatus = "Needs input";

  let lastOpenedId: string | null = null;
  $: if (task && task.id !== lastOpenedId) {
    lastOpenedId = task.id;
    name = task.name;
    wbsId = task.wbsId ?? null;
    type = task.type;
    phase = task.phase;
    preds = [...task.preds];
    o = task.o; m = task.m; p = task.p;
    pinnedDay = task.pinnedDay != null && !isNaN(task.pinnedDay) ? String(task.pinnedDay) : "";
    actualStart = task.actualStart != null && !isNaN(task.actualStart) ? String(task.actualStart) : "";
    actualFinish = task.actualFinish != null && !isNaN(task.actualFinish) ? String(task.actualFinish) : "";
    pinnedShift = task.pinnedShift ?? null;
    doubleShift = task.doubleShift ?? false;
    weekendWork = task.weekendWork ?? "auto";
    subcontractors = [...task.subcontractors];
    responsibleParty = task.responsibleParty;
    equipmentList = splitList(task.requiredEquipment);
    personnelList = splitList(task.requiredPersonnel);
    requiredStatus = task.requiredStatus.map((r) => ({ ...r }));
    riskText = task.risk;
    activeSubTab = "details";
    linkRiskId = "";
    expandedRiskIds = new Set();
    showNewRiskForm = false;
    newRiskDesc = ""; newRiskProbability = ""; newRiskImpact = ""; newRiskOwner = ""; newRiskMitigation = ""; newRiskStatus = "Needs input";
    predSearch = "";
  }
  $: if (!task) lastOpenedId = null;

  $: linkedRisks = risksLinkedInText(riskText, $risks);
  $: linkedRiskIds = new Set(linkedRisks.map((r) => r.id));
  $: linkableRisks = $risks.filter((r) => !linkedRiskIds.has(r.id));

  $: durationsDisabled = type === "milestone";

  // The current value stays selectable even if it's since been renamed or removed
  // from the project's swimlanes — saving without touching this field shouldn't
  // silently reassign the task to whatever now happens to be first in the list.
  $: phaseOptions = phase && !$projectPhases.some((p) => p.name === phase)
    ? [phase, ...$projectPhases.map((p) => p.name)]
    : $projectPhases.map((p) => p.name);

  $: predCandidates = task ? $tasks.filter((c) => c.id !== task.id) : [];
  $: predSearchResults = (() => {
    const q = predSearch.trim().toLowerCase();
    if (!q) return [];
    return predCandidates
      .filter((c) => !preds.includes(c.id))
      .filter((c) => `${wbsLabel(c, $wbsNodes)} ${c.name}`.toLowerCase().includes(q))
      .slice(0, 8);
  })();
  $: predChips = preds
    .map((id) => predCandidates.find((c) => c.id === id))
    .filter((c): c is Task => !!c);

  $: computed = (() => {
    if (!task || !$det.ok || $det.ES[task.id] === undefined) return null;
    return {
      es: fmt1($det.ES[task.id]),
      ef: fmt1($det.EF[task.id]),
      critical: $det.critical[task.id],
      slack: fmt1($det.LS[task.id] - $det.ES[task.id]),
    };
  })();

  function durationMarkPct(oo: number, mm: number, pp: number): number {
    if (pp <= oo) return 50;
    return Math.min(100, Math.max(0, ((mm - oo) / (pp - oo)) * 100));
  }

  function onTypeChange(e: Event) {
    type = (e.currentTarget as HTMLSelectElement).value as TaskType;
    if (type === "milestone") { o = 0; m = 0; p = 0; doubleShift = false; }
  }

  function save() {
    if (!task || !isEditor()) return;
    if (wbsId && !workPackages($wbsNodes).some((n) => n.id === wbsId)) { showToast("Select a valid WBS work package"); return; }
    if (!name.trim()) { showToast("Enter an activity name"); return; }
    if (type !== "milestone" && (![o, m, p].every((v) => Number.isFinite(v) && v >= 0) || o > m || m > p)) {
      showToast("Enter valid durations: 0 ≤ O ≤ M ≤ P"); return;
    }
    if (pinnedDay.trim() !== "" && (!Number.isInteger(Number(pinnedDay)) || Number(pinnedDay) < 0)) {
      showToast("Enter a whole start day of 0 or later"); return;
    }
    if (actualStart.trim() !== "" && (!Number.isInteger(Number(actualStart)) || Number(actualStart) < 0)) {
      showToast("Enter a whole actual start day of 0 or later"); return;
    }
    if (actualFinish.trim() !== "" && (!Number.isInteger(Number(actualFinish)) || Number(actualFinish) < 0)) {
      showToast("Enter a whole actual finish day of 0 or later"); return;
    }
    let oo = o, mm = m, pp = p;
    let shiftCoverage = doubleShift;
    if (type === "milestone") { oo = 0; mm = 0; pp = 0; shiftCoverage = false; }
    if (oo > pp) { const tmp = oo; oo = pp; pp = tmp; }
    const pin = pinnedDay.trim() === "" ? null : Math.max(0, parseFloat(pinnedDay));
    const actStart = actualStart.trim() === "" ? null : Math.max(0, parseInt(actualStart, 10));
    const actFinish = actualFinish.trim() === "" ? null : Math.max(0, parseInt(actualFinish, 10));
    const id = task.id;
    if (creating) {
      const createdId = createTask({ ...task, wbsId, name: name.trim(), type, phase,
        preds: preds.filter((id) => get(tasks).some((t) => t.id === id)),
        o: oo, m: mm, p: pp, pinnedDay: pin, pinnedShift, doubleShift: shiftCoverage, weekendWork, subcontractors: [...subcontractors],
        responsibleParty, requiredEquipment: equipmentList.join(", "), requiredPersonnel: personnelList.join(", "),
        requiredStatus: [...requiredStatus],
        actualStart: actStart, actualFinish: actFinish,
        risk: riskText.trim() });
      if (createdId) { closeEditModal(); showToast("Activity added"); }
      return;
    }
    tasks.update((list) =>
      list.map((t) =>
        t.id === id
          ? { ...t, wbsId, name: name.trim() || t.name, type, phase, preds: [...preds], o: oo, m: mm, p: pp, pinnedDay: pin, pinnedShift, doubleShift: shiftCoverage, weekendWork, subcontractors: [...subcontractors], responsibleParty, requiredEquipment: equipmentList.join(", "), requiredPersonnel: personnelList.join(", "), requiredStatus: [...requiredStatus], actualStart: actStart, actualFinish: actFinish, risk: riskText.trim() }
          : t
      )
    );
    scheduleSave();
    closeEditModal();
    showToast("Activity saved");
  }

  function del() {
    if (!task || creating || !isEditor()) return;
    const id = task.id;
    tasks.update((list) => list.filter((t) => t.id !== id).map((t) => ({ ...t, preds: t.preds.filter((p2) => p2 !== id) })));
    scheduleSave();
    closeEditModal();
    showToast("Activity removed");
  }

  function onPinnedDayInput(e: Event) {
    pinnedDay = (e.currentTarget as HTMLInputElement).value;
  }

  function onActualStartInput(e: Event) {
    actualStart = (e.currentTarget as HTMLInputElement).value;
  }

  function onActualFinishInput(e: Event) {
    actualFinish = (e.currentTarget as HTMLInputElement).value;
  }

  function onShiftCoverageChange(e: Event) {
    const v = selectValue(e);
    doubleShift = v === "both";
    if (v === "2") pinnedShift = 2;
    else if (v === "1") pinnedShift = 1;
  }

  function onWeekendWorkChange(e: Event) {
    weekendWork = selectValue(e) as WeekendMode;
  }

  /** "" (no requirement) | "on" | "off" — what the form shows for one status row. */
  function statusRequirement(rowId: string): "" | "on" | "off" {
    const req = requiredStatus.find((r) => r.rowId === rowId);
    return req === undefined ? "" : req.value ? "on" : "off";
  }
  function setStatusRequirement(rowId: string, choice: string) {
    const rest = requiredStatus.filter((r) => r.rowId !== rowId);
    requiredStatus = choice === "" ? rest : [...rest, { rowId, value: choice === "on" }];
  }

  function addPred(predId: string) {
    if (!preds.includes(predId)) preds = [...preds, predId];
    predSearch = "";
  }
  function removePred(predId: string) {
    preds = preds.filter((p) => p !== predId);
  }

  function toggleRiskExpand(id: string) {
    const next = new Set(expandedRiskIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    expandedRiskIds = next;
  }
  function onRiskProbabilityChange(id: string, value: string) {
    updateRisk(id, { probability: value as ProbabilityImpact });
  }
  function onRiskImpactChange(id: string, value: string) {
    updateRisk(id, { impact: value as ProbabilityImpact });
  }
  function onRiskStatusChange(id: string, value: string) {
    updateRisk(id, { status: value as RiskStatus });
  }
  function linkExistingRisk() {
    if (!isEditor() || !linkRiskId) return;
    const risk = get(risks).find((r) => r.id === linkRiskId);
    if (!risk) return;
    riskText = addRiskReference(riskText, risk);
    linkRiskId = "";
  }
  function unlinkRisk(risk: RiskEntry) {
    if (!isEditor()) return;
    riskText = removeRiskReference(riskText, risk);
  }
  function submitNewRisk() {
    if (!isEditor()) return;
    if (!newRiskDesc.trim()) { showToast("Enter a risk description"); return; }
    const risk = addRisk({
      desc: newRiskDesc.trim(), probability: newRiskProbability, impact: newRiskImpact,
      owner: newRiskOwner.trim(), mitigation: newRiskMitigation.trim(), status: newRiskStatus,
    });
    riskText = addRiskReference(riskText, risk);
    showToast("Risk added to register and linked");
    showNewRiskForm = false;
    newRiskDesc = ""; newRiskProbability = ""; newRiskImpact = ""; newRiskOwner = ""; newRiskMitigation = ""; newRiskStatus = "Needs input";
  }

  function onOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) closeEditModal();
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") closeEditModal();
  }
</script>

<svelte:window on:keydown={onKeydown} />

{#if task}
  <div class="modal-overlay" on:click={onOverlayClick} role="presentation">
    <div class="modal modal--activity" role="dialog" aria-modal="true" aria-labelledby="editModalTitle">
      <div class="modal-head">
        <div class="modal-title" id="editModalTitle">
          {creating ? "Add activity" : (task.type === "milestone" ? "Edit milestone — " : "Edit activity — ") + task.name}{readOnly ? " (view only)" : ""}
        </div>
        <button class="modal-close" type="button" aria-label="Close" on:click={closeEditModal}>✕</button>
      </div>
      <div class="modal-subtabs" role="tablist">
        <button type="button" class="modal-subtab" class:active={activeSubTab === "details"} role="tab" aria-selected={activeSubTab === "details"}
          on:click={() => activeSubTab = "details"}>Details</button>
        <button type="button" class="modal-subtab" class:active={activeSubTab === "risks"} role="tab" aria-selected={activeSubTab === "risks"}
          on:click={() => activeSubTab = "risks"}>
          Risks{#if linkedRisks.length}<span class="count">{linkedRisks.length}</span>{/if}
        </button>
      </div>
      {#if activeSubTab === "details"}
      <div class="modal-body">
        <label for="activity-wbs">WBS work package
          <WbsSelect id="activity-wbs" value={wbsId} disabled={readOnly} on:change={(e) => wbsId = e.detail} />
          <span class="modal-hint">Scope stays the same when moving between phase swimlanes. Shared or unclear scope can remain unassigned.</span>
        </label>
        <label>Name<input type="text" bind:value={name} disabled={readOnly} /></label>

        <div class="modal-columns">
          <div class="modal-column">
            <div class="modal-section-head">Activity &amp; scheduling</div>

            <div class="modal-row">
              <label>Type
                <select bind:value={type} disabled={readOnly} on:change={onTypeChange}>
                  <option value="activity">Activity</option>
                  <option value="milestone">Milestone</option>
                </select>
              </label>
              <label>Phase
                <select bind:value={phase} disabled={readOnly}>
                  {#each phaseOptions as ph}
                    <option value={ph}>{ph}</option>
                  {/each}
                </select>
              </label>
            </div>

            <div class="duration-group">
              <span class="field-label">Duration ({$twoShifts ? "shifts" : "days"})</span>
              <div class="modal-row">
                <label><span class="modal-hint">Optimistic</span>
                  <input type="number" min="0" step="0.5" bind:value={o} disabled={readOnly || durationsDisabled} />
                </label>
                <label><span class="modal-hint">Most likely</span>
                  <input type="number" min="0" step="0.5" bind:value={m} disabled={readOnly || durationsDisabled} />
                </label>
                <label><span class="modal-hint">Pessimistic</span>
                  <input type="number" min="0" step="0.5" bind:value={p} disabled={readOnly || durationsDisabled} />
                </label>
              </div>
              {#if !durationsDisabled}
                {#if o === p}
                  <div class="range-bar">
                    <div class="mark m" style="left:50%"></div>
                  </div>
                {:else}
                  <div class="range-bar">
                    <div class="fill" style="left:0%; right:0%"></div>
                    <div class="mark op" style="left:0%"></div>
                    <div class="mark m" style="left:{durationMarkPct(o, m, p)}%"></div>
                    <div class="mark op" style="left:100%"></div>
                  </div>
                {/if}
                <div class="range-caption">
                  <span>{o === p ? "no spread — fixed duration" : "optimistic → pessimistic range"}</span>
                  <span class="mono">{fmt1(o)}–{fmt1(m)}–{fmt1(p)}{$twoShifts ? "sh" : "d"}</span>
                </div>
              {/if}
            </div>

            <label>Start no earlier than (day)
              <input type="number" min="0" step="1" value={pinnedDay} placeholder="auto — from predecessors" disabled={readOnly}
                on:input={onPinnedDayInput} />
              <span class="modal-hint">Set by dragging on the Lean planning board, or type a day here. Leave blank for automatic scheduling.</span>
            </label>

            <div class="modal-row">
              <label>Actual start (day)
                <input type="number" min="0" step="1" value={actualStart} placeholder="auto — from board" disabled={readOnly}
                  on:input={onActualStartInput} />
              </label>
              <label>Actual finish (day)
                <input type="number" min="0" step="1" value={actualFinish} placeholder="auto — from board" disabled={readOnly}
                  on:input={onActualFinishInput} />
              </label>
            </div>
            <span class="modal-hint">Normally left blank — the day-by-day board already derives actual start/finish from days marked done. Set these only to correct that, or to record actual dates for work tracked outside the board (e.g. a P6 import).</span>

            {#if $twoShifts && !durationsDisabled}
              <label>Shift coverage
                <select value={doubleShift ? "both" : pinnedShift === 2 ? "2" : "1"} disabled={readOnly} on:change={onShiftCoverageChange}>
                  <option value="1">Shift 1 ({SHIFT_LABEL[1]}) — one team</option>
                  <option value="2">Shift 2 ({SHIFT_LABEL[2]}) — one team</option>
                  <option value="both">Shift 1 + 2 ({SHIFT_LABEL[1]} & {SHIFT_LABEL[2]}) — two teams</option>
                </select>
                <span class="modal-hint">Whether a single team works this activity on shift 1 or shift 2, or two teams work it at once, one per shift — which also halves its computed duration.</span>
              </label>
            {/if}

            {#if !durationsDisabled}
              <label>Weekend work
                <select value={weekendWork} disabled={readOnly} on:change={onWeekendWorkChange}>
                  <option value="auto">Follow project setting — currently {$allowWeekendWork ? "allowed" : "not allowed"}</option>
                  <option value="never">Never schedule on a weekend</option>
                  <option value="always">Always allow weekend work</option>
                </select>
                <span class="modal-hint">Overrides the project's weekend-work setting for this activity only — e.g. force it on for an urgent activity, or off for one that can't run unattended.</span>
              </label>
            {/if}

            <div class="field-block">
              <span class="field-label">Predecessors <span class="opt-tag">(optional)</span></span>
              <div class="chip-list">
                {#each predChips as cand (cand.id)}
                  <span class="chip pred">
                    {wbsLabel(cand, $wbsNodes)} — {cand.name}
                    {#if !readOnly}<button type="button" aria-label="Remove predecessor" on:click={() => removePred(cand.id)}>✕</button>{/if}
                  </span>
                {/each}
              </div>
              {#if !readOnly}
                <div class="search-field">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.4"/><path d="M13.5 13.5L11 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                  <input type="text" bind:value={predSearch} placeholder="Search activities…" />
                </div>
                {#if predSearchResults.length}
                  <div class="pred-results">
                    {#each predSearchResults as cand (cand.id)}
                      <button type="button" class="pred-result-item" on:click={() => addPred(cand.id)}>
                        {wbsLabel(cand, $wbsNodes)} — {cand.name}
                      </button>
                    {/each}
                  </div>
                {/if}
              {/if}
            </div>
          </div>

          <div class="modal-column">
            <div class="modal-section-head">People</div>

            <ChipPicker
              label="Supporting contractors"
              itemNoun="subcontractor"
              bind:selected={subcontractors}
              known={$knownSubcontractors}
              {readOnly}
              newPlaceholder="Or add a new subcontractor…"
              colorOf={(sc) => subcontractorColor($subcontractorColors, sc)}
              onAddKnown={addKnownSubcontractor}
              onRemoveKnown={removeKnownSubcontractor}
            />

            <label>Responsible party
              <select bind:value={responsibleParty} disabled={readOnly}>
                {#each RESPONSIBLE_PARTY_OPTIONS as rp}
                  <option value={rp}>{rp || "— set —"}</option>
                {/each}
              </select>
            </label>
          </div>

          <div class="modal-column">
            <div class="modal-section-head">Resources</div>

            <ChipPicker
              label="Required equipment"
              itemNoun="equipment item"
              bind:selected={equipmentList}
              known={$knownEquipment}
              {readOnly}
              newPlaceholder="e.g. Cherry picker, AGV"
              onAddKnown={addKnownEquipment}
              onRemoveKnown={removeKnownEquipment}
            />

            <ChipPicker
              label="Required supporting personnel"
              itemNoun="person"
              bind:selected={personnelList}
              known={$knownPersonnel}
              {readOnly}
              newPlaceholder="e.g. Automation engineer, IT support"
              onAddKnown={addKnownPersonnel}
              onRemoveKnown={removeKnownPersonnel}
            />

            {#if $statusRows.length}
              <div class="field-block">
                <span class="field-label">Requires site status <span class="opt-tag">(optional)</span></span>
                <span class="modal-hint">Flags this card when the site board disagrees — it never changes the status itself.</span>
                {#each $statusRows as row (row.id)}
                  <div class="add-inline">
                    <span class="status-req-label">{row.label}</span>
                    <select value={statusRequirement(row.id)} disabled={readOnly}
                      on:change={(e) => setStatusRequirement(row.id, selectValue(e))}>
                      <option value="">No requirement</option>
                      <option value="on">Must be {row.onLabel}</option>
                      <option value="off">Must be {row.offLabel}</option>
                    </select>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </div>

        {#if computed}
          <div class="schedule-bar">
            <div class="label-col">
              <span class="k">Calculated schedule — from earliest start{pinnedDay.trim() !== "" ? ` (day ${pinnedDay.trim()})` : ""}</span>
              <span class="v mono">Day {computed.es} → Day {computed.ef}</span>
            </div>
            <div class="divider-v"></div>
            <div class="label-col">
              <span class="k">{computed.critical ? "Critical path" : "Total float"}</span>
              <span class="v mono">{computed.critical ? "0 days slack" : `${computed.slack} days`}</span>
            </div>
          </div>
        {/if}
      </div>
      {/if}
      {#if activeSubTab === "risks"}
      <div class="modal-body">
        <div class="modal-section-head">Linked risks</div>
        {#if linkedRisks.length}
          <div class="risk-link-list">
            {#each linkedRisks as risk (risk.id)}
              {@const level = riskLevel(risk)}
              {@const expanded = expandedRiskIds.has(risk.id)}
              <div class="risk-link-row" class:expanded>
                <button class="risk-link-head" type="button" aria-expanded={expanded} on:click={() => toggleRiskExpand(risk.id)}>
                  <span class="risk-link-chevron">{expanded ? "▾" : "▸"}</span>
                  <div class="risk-link-main">
                    <div class="risk-link-id mono">{risk.id}</div>
                    <div class="risk-link-desc">{risk.desc || "(no description)"}</div>
                    <div class="risk-link-badges">
                      <span class="risk-score" class:low={level === "low"} class:medium={level === "medium"} class:high={level === "high"} class:unset={!level}>{level || "—"}</span>
                      <span class="pill">{risk.status}</span>
                    </div>
                  </div>
                </button>
                {#if !readOnly}
                  <button class="rowbtn" type="button" title="Unlink risk" on:click={() => unlinkRisk(risk)}>✕</button>
                {/if}
                {#if expanded}
                  <div class="risk-edit-fields">
                    <label>Description
                      <input type="text" value={risk.desc} placeholder="What could go wrong?" disabled={readOnly}
                        on:change={(e) => updateRisk(risk.id, { desc: inputValue(e).trim() })} />
                    </label>
                    <div class="modal-row">
                      <label>Probability
                        <select disabled={readOnly} on:change={(e) => onRiskProbabilityChange(risk.id, selectValue(e))}>
                          {#each PROBABILITY_IMPACT_OPTIONS as opt}
                            <option value={opt} selected={risk.probability === opt}>{opt || "— set —"}</option>
                          {/each}
                        </select>
                      </label>
                      <label>Impact
                        <select disabled={readOnly} on:change={(e) => onRiskImpactChange(risk.id, selectValue(e))}>
                          {#each PROBABILITY_IMPACT_OPTIONS as opt}
                            <option value={opt} selected={risk.impact === opt}>{opt || "— set —"}</option>
                          {/each}
                        </select>
                      </label>
                    </div>
                    <div class="modal-row">
                      <label>Owner
                        <input type="text" value={risk.owner} placeholder="e.g. Site manager" disabled={readOnly}
                          on:change={(e) => updateRisk(risk.id, { owner: inputValue(e).trim() })} />
                      </label>
                      <label>Status
                        <select disabled={readOnly} on:change={(e) => onRiskStatusChange(risk.id, selectValue(e))}>
                          {#each RISK_STATUS_OPTIONS as opt}
                            <option value={opt} selected={risk.status === opt}>{opt}</option>
                          {/each}
                        </select>
                      </label>
                    </div>
                    <label>Mitigation
                      <input type="text" value={risk.mitigation} placeholder="Optional" disabled={readOnly}
                        on:change={(e) => updateRisk(risk.id, { mitigation: inputValue(e).trim() })} />
                    </label>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {:else}
          <div class="risk-empty">No risks linked to this activity yet.</div>
        {/if}

        {#if !readOnly}
          <div class="risk-link-add">
            <select bind:value={linkRiskId} disabled={!linkableRisks.length}>
              <option value="">{linkableRisks.length ? "Link an existing risk…" : "All risks are already linked"}</option>
              {#each linkableRisks as r (r.id)}
                <option value={r.id}>{r.id} — {r.desc || "(no description)"}</option>
              {/each}
            </select>
            <button class="btn" type="button" disabled={!linkRiskId} on:click={linkExistingRisk}>Link</button>
          </div>

          {#if !showNewRiskForm}
            <button class="btn" type="button" on:click={() => showNewRiskForm = true}>+ New risk</button>
          {:else}
            <div class="new-risk-form">
              <label>Description<input type="text" bind:value={newRiskDesc} placeholder="What could go wrong?" /></label>
              <div class="modal-row">
                <label>Probability
                  <select bind:value={newRiskProbability}>
                    {#each PROBABILITY_IMPACT_OPTIONS as opt}<option value={opt}>{opt || "— set —"}</option>{/each}
                  </select>
                </label>
                <label>Impact
                  <select bind:value={newRiskImpact}>
                    {#each PROBABILITY_IMPACT_OPTIONS as opt}<option value={opt}>{opt || "— set —"}</option>{/each}
                  </select>
                </label>
              </div>
              <div class="modal-row">
                <label>Owner<input type="text" bind:value={newRiskOwner} placeholder="e.g. Site manager" /></label>
                <label>Status
                  <select bind:value={newRiskStatus}>
                    {#each RISK_STATUS_OPTIONS as opt}<option value={opt}>{opt}</option>{/each}
                  </select>
                </label>
              </div>
              <label>Mitigation<input type="text" bind:value={newRiskMitigation} placeholder="Optional" /></label>
              <div class="new-risk-form-actions">
                <button class="btn" type="button" on:click={() => showNewRiskForm = false}>Cancel</button>
                <button class="btn primary" type="button" on:click={submitNewRisk}>Add &amp; link</button>
              </div>
            </div>
          {/if}
        {/if}
      </div>
      {/if}
      <div class="modal-foot">
        {#if !readOnly && !creating}
          <button class="btn danger" type="button" on:click={del}>Delete</button>
        {/if}
        <div class="spacer"></div>
        <button class="btn" type="button" on:click={closeEditModal}>Cancel</button>
        {#if !readOnly}
          <button class="btn primary" type="button" on:click={save}>{creating ? "Add activity" : "Save"}</button>
        {/if}
      </div>
    </div>
  </div>
{/if}
